/**
 * Field Calculator — write one expression, run it across every row (or just
 * the selected ones) in the attribute table, same idea as Esri's own Field
 * Calculator.
 *
 * Two tabs, SQL and Python, for familiarity — see fieldCalculator.ts's own
 * doc comment for why this is one safe expression evaluator underneath,
 * not two real engines: the only thing the tab actually changes is which
 * field-reference syntax reads naturally (`[Field]` vs `!Field!`) and which
 * word spells "and"/"or"/"not". Every operator and function works the same
 * either way, and nothing here calls `eval`/`new Function` on what someone
 * typed.
 */

import { useMemo, useState } from 'react';
import { Calculator, Loader2, X } from 'lucide-react';
import type { Layer } from '../../types';
import { btnClass, Field, inputClass, Notice } from '../tools/panels/shared';
import {
  calculateField,
  type EditableField,
  type NewFieldDef,
  addFieldToLayer,
} from '../../layers/featureEditing';
import { compileExpression, FIELD_CALCULATOR_FUNCTIONS } from '../../layers/fieldCalculator';
import type { AttributeFeature } from './FloatingAttributeTable';

type Dialect = 'sql' | 'python';

export default function FieldCalculatorDialog({
  layer,
  fields,
  objectIdField,
  records,
  selectedIds,
  initialField,
  moduleId,
  arcgisUrl,
  onClose,
  onApplied,
  onFieldAddedRemotely,
}: {
  layer: Layer;
  /** Every field this layer can actually be written to — `editInfo.fields`,
   *  already loaded by the table for its own edit modal. */
  fields: EditableField[];
  objectIdField: string;
  /** Every loaded row, not just the ones currently visible — a calculation
   *  scoped to "All rows" means every row the table has, independent of
   *  whatever search text happens to be in the box right now. */
  records: AttributeFeature[];
  selectedIds: Set<string>;
  initialField?: string;
  moduleId?: string;
  arcgisUrl: string;
  onClose: () => void;
  /** `targetFieldName` is whichever field actually got written to — the
   *  caller cannot assume this matches `initialField`: "New field…" mode,
   *  or simply picking a different field from the dropdown, both change it
   *  from whatever pre-fill the caller opened the dialog with. */
  onApplied: (
    updatedLayer: Partial<Layer> | undefined,
    targetFieldName: string,
    writes: { id: string; value: unknown }[],
  ) => void;
  /** Fires after "New field…" successfully adds a field to an ARCGIS layer
   *  specifically — the one case `addFieldToLayer` has no `updatedLayer` to
   *  hand back at all (the service, not `layers` state, is the source of
   *  truth), so the caller's own `editInfo` (read once, cached) has no
   *  other way to learn the new field exists before the write that follows
   *  targets it. Mirrors `FloatingAttributeTable`'s own `schemaRefreshToken`
   *  bump after its "Add field…" menu item does the same thing. */
  onFieldAddedRemotely?: () => void;
}) {
  const existingNames = useMemo(() => new Set(fields.map((field) => field.name)), [fields]);
  const [mode, setMode] = useState<'existing' | 'new'>(
    initialField && existingNames.has(initialField) ? 'existing' : fields.length > 0 ? 'existing' : 'new',
  );
  const [targetName, setTargetName] = useState(initialField && existingNames.has(initialField) ? initialField : fields[0]?.name ?? '');
  const [newFieldName, setNewFieldName] = useState(initialField && !existingNames.has(initialField) ? initialField : '');
  const [newFieldType, setNewFieldType] = useState<NewFieldDef['type']>('string');

  const [dialect, setDialect] = useState<Dialect>('sql');
  const [expression, setExpression] = useState('');
  const [scope, setScope] = useState<'all' | 'selected'>(selectedIds.size > 0 ? 'selected' : 'all');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const rowsInScope = scope === 'selected' ? records.filter((record) => selectedIds.has(record.id)) : records;

  const fieldHint = dialect === 'sql' ? '[FieldName]' : '!FieldName!';
  const placeholder =
    dialect === 'sql'
      ? `e.g.  ${fields[0]?.name ? `[${fields[0].name}] * 3.28084` : '[LENGTH_M] * 3.28084'}`
      : `e.g.  ${fields[0]?.name ? `!${fields[0].name}! * 3.28084` : '!LENGTH_M! * 3.28084'}`;

  /** A live preview against the first row in scope — the fastest way to
   *  catch a typo before running it across a thousand rows. */
  const preview = useMemo(() => {
    if (!expression.trim() || rowsInScope.length === 0) return null;
    try {
      const compiled = compileExpression(expression);
      const value = compiled.run(rowsInScope[0].properties);
      return { ok: true as const, value };
    } catch (failure) {
      return { ok: false as const, message: failure instanceof Error ? failure.message : 'Could not parse that.' };
    }
  }, [expression, rowsInScope]);

  const insertField = (name: string) => {
    const token = dialect === 'sql' ? `[${name}]` : `!${name}!`;
    setExpression((current) => (current ? `${current} ${token}` : token));
  };

  const run = async () => {
    setError(null);
    setResult(null);

    const targetField: EditableField =
      mode === 'existing'
        ? fields.find((field) => field.name === targetName) ?? { name: targetName, alias: targetName, type: 'string' }
        : { name: newFieldName.trim(), alias: newFieldName.trim(), type: newFieldType };

    if (!targetField.name) {
      setError(mode === 'new' ? 'Enter a name for the new field.' : 'Choose a field to calculate.');
      return;
    }
    if (mode === 'new' && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(targetField.name)) {
      setError('Use letters, numbers and underscores only, starting with a letter.');
      return;
    }
    if (rowsInScope.length === 0) {
      setError(scope === 'selected' ? 'No rows are selected.' : 'There are no rows to calculate.');
      return;
    }

    let compiled;
    try {
      compiled = compileExpression(expression);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not parse that expression.');
      return;
    }

    setBusy(true);
    try {
      // A brand-new field has to exist on the SERVICE's own schema before
      // `applyEdits` can write to it — but only for an ArcGIS-backed layer.
      // A local layer has no schema at all: `calculateField` below already
      // adds the field as it writes each row's value, same as
      // `addFieldToLayer`'s own local branch would, so calling that
      // separately here would only create a race against it — two
      // successive `onLayerChange` calls, the second (the real calculated
      // values, scoped to fewer rows than "every feature") overwriting
      // whatever the first had just added everywhere else.
      if (mode === 'new' && !layer.localData) {
        const added = await addFieldToLayer(
          layer,
          { name: targetField.name, alias: targetField.name, type: newFieldType },
          arcgisUrl,
        );
        if (!added.success) {
          setError(added.message);
          setBusy(false);
          return;
        }
        onFieldAddedRemotely?.();
      }

      const writes: { id: string; key?: string; value: unknown }[] = [];
      const evalErrors: string[] = [];
      for (const record of rowsInScope) {
        try {
          writes.push({ id: record.id, key: record.key, value: compiled.run(record.properties) });
        } catch (failure) {
          evalErrors.push(failure instanceof Error ? failure.message : 'Could not evaluate that row.');
        }
      }
      if (writes.length === 0) {
        setError(evalErrors[0] ?? 'Nothing could be calculated.');
        setBusy(false);
        return;
      }

      const outcome = await calculateField(layer, targetField, writes, objectIdField, moduleId, arcgisUrl);
      const succeeded = writes.filter((write) => !outcome.failedIds.has(write.key ?? write.id));
      if (succeeded.length > 0) onApplied(outcome.updatedLayer, targetField.name, succeeded);

      if (!outcome.success && succeeded.length === 0) {
        setError(outcome.message);
      } else {
        setResult(outcome.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="gl-modal-backdrop fixed inset-0 z-[4500] flex items-center justify-center p-4"
      onPointerDown={(event) => event.stopPropagation()}
      onClick={() => !busy && onClose()}
    >
      <div
        role="dialog"
        aria-label="Field Calculator"
        onClick={(event) => event.stopPropagation()}
        className="gl-modal flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <h3 className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-800">
            <Calculator className="h-4 w-4 text-blue-600" />
            Field Calculator
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {/* ── Target field ── */}
          <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
            <button
              type="button"
              onClick={() => setMode('existing')}
              disabled={fields.length === 0}
              className={`flex-1 rounded px-2 py-1 transition disabled:opacity-40 ${
                mode === 'existing' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Existing field
            </button>
            <button
              type="button"
              onClick={() => setMode('new')}
              className={`flex-1 rounded px-2 py-1 transition ${
                mode === 'new' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              New field…
            </button>
          </div>

          {mode === 'existing' ? (
            <Field label="Field to calculate">
              <select value={targetName} onChange={(event) => setTargetName(event.target.value)} className={inputClass}>
                {fields.length === 0 && <option value="">No editable fields on this layer</option>}
                {fields.map((field) => (
                  <option key={field.name} value={field.name}>
                    {field.alias} {field.alias !== field.name ? `(${field.name})` : ''}
                  </option>
                ))}
              </select>
            </Field>
          ) : (
            <div className="flex gap-2">
              <div className="flex-1">
                <Field label="New field name">
                  <input
                    value={newFieldName}
                    onChange={(event) => setNewFieldName(event.target.value)}
                    placeholder="e.g. length_ft"
                    className={inputClass}
                  />
                </Field>
              </div>
              <div className="w-28 flex-shrink-0">
                <Field label="Type">
                  <select
                    value={newFieldType}
                    onChange={(event) => setNewFieldType(event.target.value as NewFieldDef['type'])}
                    className={inputClass}
                  >
                    <option value="string">Text</option>
                    <option value="number">Number</option>
                    <option value="boolean">Yes / No</option>
                  </select>
                </Field>
              </div>
            </div>
          )}

          {/* ── Expression language ── */}
          <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
            <button
              type="button"
              onClick={() => setDialect('sql')}
              className={`flex-1 rounded px-2 py-1 transition ${
                dialect === 'sql' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              SQL
            </button>
            <button
              type="button"
              onClick={() => setDialect('python')}
              className={`flex-1 rounded px-2 py-1 transition ${
                dialect === 'python' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Python
            </button>
          </div>

          <Field
            label="Expression"
            hint={`Reference a field as ${fieldHint}. AND / OR / NOT, + − × ÷ %, comparisons, and ${FIELD_CALCULATOR_FUNCTIONS.join(', ')}.`}
          >
            <textarea
              value={expression}
              onChange={(event) => setExpression(event.target.value)}
              placeholder={placeholder}
              rows={3}
              className={`${inputClass} resize-none font-mono`}
            />
          </Field>

          {fields.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {fields.map((field) => (
                <button
                  key={field.name}
                  type="button"
                  onClick={() => insertField(field.name)}
                  className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10.5px] font-medium text-slate-600 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                >
                  {field.name}
                </button>
              ))}
            </div>
          )}

          {preview && (
            <p
              className={`rounded-lg px-3 py-2 text-[11.5px] ${
                preview.ok ? 'bg-slate-50 text-slate-600' : 'bg-amber-50 text-amber-700'
              }`}
            >
              {preview.ok ? (
                <>
                  Preview (first row): <span className="font-mono font-semibold">{String(preview.value)}</span>
                </>
              ) : (
                preview.message
              )}
            </p>
          )}

          {/* ── Scope ── */}
          <div className="flex gap-1 rounded-md bg-slate-100 p-0.5 text-[11px] font-medium">
            <button
              type="button"
              onClick={() => setScope('all')}
              className={`flex-1 rounded px-2 py-1 transition ${
                scope === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              All rows ({records.length})
            </button>
            <button
              type="button"
              onClick={() => setScope('selected')}
              disabled={selectedIds.size === 0}
              className={`flex-1 rounded px-2 py-1 transition disabled:opacity-40 ${
                scope === 'selected' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              Selected only ({selectedIds.size})
            </button>
          </div>

          {error && <Notice tone="warn">{error}</Notice>}
          {result && <Notice>{result}</Notice>}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
          <button type="button" onClick={onClose} disabled={busy} className={`${btnClass} gl-btn--ghost`}>
            Close
          </button>
          <button
            type="button"
            onClick={() => void run()}
            disabled={busy || !expression.trim() || rowsInScope.length === 0}
            className={`${btnClass} gl-btn--primary`}
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {busy ? 'Calculating…' : `Calculate ${rowsInScope.length} row${rowsInScope.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
